import React, { useEffect, useRef, useState } from 'react'
import { Upload, Image, Button } from 'antd'
import {
  UploadOutlined,
  LeftOutlined,
  RightOutlined,
  PlusOutlined,
  DownOutlined,
  CameraOutlined,
} from '@ant-design/icons'

/**
 * Imágenes de empaque del material: subir, tomar con la cámara, ver y eliminar.
 * Usa el mismo carrusel (visor + miniaturas) que las fotos del material.
 */
const ImagenesEmpaqueCard = ({
  images,
  isReadOnly,
  basculaConectada,
  onUpload,
  onDelete,
  onTomarFoto,
}) => {
  const imagenesConUrl = images.filter(img => img?.signed_url)
  const total = imagenesConUrl.length

  const [indiceActivo, setIndiceActivo] = useState(0)
  const [abierto, setAbierto] = useState(false)
  const [previewVisible, setPreviewVisible] = useState(false)
  const totalAnterior = useRef(total)
  const thumbsRef = useRef(null)

  // Al agregar imágenes se salta a la última; al eliminar se ajusta el índice.
  useEffect(() => {
    if (total > totalAnterior.current && totalAnterior.current > 0) {
      setIndiceActivo(total - 1)
    } else if (indiceActivo > total - 1) {
      setIndiceActivo(Math.max(total - 1, 0))
    }
    totalAnterior.current = total
  }, [total])

  useEffect(() => {
    const thumb = thumbsRef.current?.children?.[indiceActivo]
    thumb?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
  }, [indiceActivo])

  const irAnterior = () => setIndiceActivo(i => (i - 1 + total) % total)
  const irSiguiente = () => setIndiceActivo(i => (i + 1) % total)

  // Se retorna false para que antd no haga su propio POST (lo hace onUpload).
  const beforeUpload = (file) => {
    onUpload(file)
    return false
  }

  const imagenActiva = imagenesConUrl[indiceActivo]

  const renderVisor = () => {
    if (!imagenActiva) {
      return (
        <Upload
          listType="picture-card"
          showUploadList={false}
          beforeUpload={beforeUpload}
          multiple
          accept="image/*"
          className="compact-upload photos-carousel-empty"
          disabled={isReadOnly}
        >
          <div style={{ textAlign: 'center' }}>
            <UploadOutlined style={{ fontSize: 18, color: '#1890ff' }} />
            <div style={{ marginTop: 4, color: '#999', fontSize: '11px' }}>Subir imágenes de empaque</div>
          </div>
        </Upload>
      )
    }

    return (
      <div className="photos-carousel-viewer">
        <Image
          src={imagenActiva.signed_url}
          alt={imagenActiva.nombre_objeto}
          preview={false}
          onClick={() => setPreviewVisible(true)}
        />
        <Image.PreviewGroup
          items={imagenesConUrl.map(img => img.signed_url)}
          preview={{
            visible: previewVisible,
            onVisibleChange: (visible) => setPreviewVisible(visible),
            current: indiceActivo,
            onChange: (current) => setIndiceActivo(current),
          }}
        />

        {total > 1 && (
          <>
            <button
              type="button"
              className="photos-carousel-arrow is-prev"
              onClick={irAnterior}
              title="Anterior"
            >
              <LeftOutlined />
            </button>
            <button
              type="button"
              className="photos-carousel-arrow is-next"
              onClick={irSiguiente}
              title="Siguiente"
            >
              <RightOutlined />
            </button>
          </>
        )}

        <span className="photos-carousel-counter">
          {indiceActivo + 1} / {total}
        </span>
      </div>
    )
  }

  return (
    <div className={`photos-card ${abierto ? '' : 'is-collapsed'}`}>
      <div className="photos-card-header">
        <button
          type="button"
          className="photos-card-toggle"
          onClick={() => setAbierto(a => !a)}
        >
          <DownOutlined className="photos-card-chevron" />
          {!abierto && imagenesConUrl[0] && (
            <img
              className="photos-card-mini"
              src={imagenesConUrl[0].signed_url}
              alt={imagenesConUrl[0].nombre_objeto}
            />
          )}
          <span className="photos-card-title">
            Imágenes empaque ({total})
          </span>
        </button>
        <Button
          type="primary"
          icon={<CameraOutlined />}
          onClick={() => {
            // La foto nueva entra al carrusel; se abre para verla.
            setAbierto(true)
            onTomarFoto()
          }}
          disabled={isReadOnly}
          size="small"
          style={{
            backgroundColor: basculaConectada ? '#52c41a' : '#d9d9d9',
            borderColor: basculaConectada ? '#52c41a' : '#d9d9d9',
          }}
          title={basculaConectada ? 'Tomar foto del empaque con cámara' : 'Socket no conectado'}
        >
          Cámara
        </Button>
      </div>
      {abierto && (
      <div className="photos-card-body photos-carousel">
        {renderVisor()}

        {imagenActiva && !isReadOnly && (
          <div className="image-actions-container">
            <button
              className="delete-image-button"
              type="button"
              onClick={() => onDelete(imagenActiva.nombre_objeto)}
            >
              Eliminar
            </button>
          </div>
        )}

        {total > 0 && (
          <div className="photos-carousel-thumbs-row">
            <div className="photos-carousel-thumbs" ref={thumbsRef}>
              {imagenesConUrl.map((img, index) => (
                <button
                  key={img.nombre_objeto}
                  type="button"
                  className={`photos-carousel-thumb ${index === indiceActivo ? 'is-active' : ''}`}
                  onClick={() => setIndiceActivo(index)}
                  title={img.nombre_objeto}
                >
                  <img src={img.signed_url} alt={img.nombre_objeto} />
                </button>
              ))}
            </div>
            {!isReadOnly && (
              <Upload
                showUploadList={false}
                beforeUpload={beforeUpload}
                multiple
                accept="image/*"
              >
                <button
                  type="button"
                  className="photos-carousel-add"
                  title="Agregar imágenes de empaque"
                >
                  <PlusOutlined />
                </button>
              </Upload>
            )}
          </div>
        )}
      </div>
      )}
    </div>
  )
}

export default ImagenesEmpaqueCard
